import { useEffect, useState, useRef } from "react";
import { LANGUAGES, getLanguageByCode } from "@/lib/languages";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { useTheme } from "@/components/ui/theme-provider";
import { Settings, Trash2, Shield, Info, User, Palette, Globe, X, ChevronLeft, ChevronRight } from "lucide-react";
import { LanguageSelector } from "@/components/LanguageSelector";
import { useUserLanguage } from "@/hooks/use-user-language";
import { useUserName } from "@/hooks/use-user-name";
import { usePronouns } from "@/hooks/use-pronouns";
import { AuthUpgradeModal } from "./AuthUpgradeModal";
import { useLocation } from "wouter";
import versionData from "../../version.json";
import ConfirmPopup from "../ui/ConfirmPopup";
import { useToast } from "@/hooks/use-toast";
import { PaletteSelector } from "@/components/ui/PaletteSelector";
import { getAuthHeaders } from "@/lib/queryClient";
import { apiUrl } from "@/lib/config";

const PRONOUN_OPTIONS: { value: string; label: string }[] = [
  { value: "she/her", label: "She/Her" },
  { value: "he/him", label: "He/Him" },
  { value: "they/them", label: "They/Them" },
  { value: "", label: "Prefer not to say" },
];

export type SettingsTab = "profile" | "appearance" | "account" | "language" | "about";

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentUser: any;
  onUpdateSettings: (settings: any) => void;
  onRoomDeleted?: () => void;
  onClearCurrentRoom?: () => void;
  autoTranslationEnabled?: boolean;
  initialSection?: string;
}

export function SettingsModal({
  isOpen,
  onClose,
  currentUser,
  onUpdateSettings,
  onRoomDeleted,
  onClearCurrentRoom,
  autoTranslationEnabled = true,
  initialSection = "profile",
}: SettingsModalProps) {
  const { theme, setTheme, palette, setPalette } = useTheme();
  const { userLanguage, updateLanguage } = useUserLanguage();
  const { userName, updateUserName } = useUserName();
  const { pronouns, updatePronouns } = usePronouns();
  const [activeTab, setActiveTab] = useState<SettingsTab>("profile");

  // Draft state variables (held locally in modal until saved)
  const [draftUserName, setDraftUserName] = useState("");
  const [draftPronouns, setDraftPronouns] = useState("");
  const [draftDarkMode, setDraftDarkMode] = useState(false);
  const [draftPalette, setDraftPalette] = useState<any>("neon-pop");
  const [draftLanguage, setDraftLanguage] = useState("en");
  const [draftAutoTranslation, setDraftAutoTranslation] = useState(autoTranslationEnabled);

  // Initial snapshot to detect unsaved changes
  const [initialSnapshot, setInitialSnapshot] = useState({
    userName: "",
    pronouns: "",
    isDarkMode: false,
    palette: "neon-pop" as any,
    language: "en",
    autoTranslation: true,
  });

  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showOriginal, setShowOriginal] = useState(true);
  const [anonymousMode, setAnonymousMode] = useState(true);
  const [showLanguageSelector, setShowLanguageSelector] = useState(false);
  const [showAuthUpgrade, setShowAuthUpgrade] = useState(false);
  const [, navigate] = useLocation();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [actionType, setActionType] = useState<"clearData" | "resetOnboarding" | null>(null);
  const { toast } = useToast();
  const tabsNavRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const prevIsOpenRef = useRef(false);

  const checkScroll = () => {
    if (tabsNavRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tabsNavRef.current;
      setCanScrollLeft(scrollLeft > 6);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 6);
    }
  };

  const scrollTabs = (direction: "left" | "right") => {
    if (tabsNavRef.current) {
      const scrollAmount = direction === "left" ? -130 : 130;
      tabsNavRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      const isDark =
        theme === "dark" ||
        (theme === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches);

      const snap = {
        userName: userName || "",
        pronouns: pronouns || "",
        isDarkMode: isDark,
        palette: palette || "neon-pop",
        language: userLanguage || "en",
        autoTranslation: autoTranslationEnabled ?? true,
      };

      setInitialSnapshot(snap);
      setDraftUserName(snap.userName);
      setDraftPronouns(snap.pronouns);
      setDraftDarkMode(snap.isDarkMode);
      setDraftPalette(snap.palette);
      setDraftLanguage(snap.language);
      setDraftAutoTranslation(snap.autoTranslation);
      setShowDiscardConfirm(false);

      setActiveTab((initialSection as SettingsTab) || "profile");
      setTimeout(checkScroll, 100);
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, theme, palette, userLanguage, userName, pronouns, autoTranslationEnabled, initialSection]);

  useEffect(() => {
    const handleResize = () => checkScroll();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const hasUnsavedChanges =
    draftUserName !== initialSnapshot.userName ||
    draftPronouns !== initialSnapshot.pronouns ||
    draftDarkMode !== initialSnapshot.isDarkMode ||
    draftPalette !== initialSnapshot.palette ||
    draftLanguage !== initialSnapshot.language ||
    draftAutoTranslation !== initialSnapshot.autoTranslation;

  const handleRequestClose = () => {
    if (hasUnsavedChanges) {
      setShowDiscardConfirm(true);
    } else {
      onClose();
    }
  };

  const handleDiscardConfirm = () => {
    setShowDiscardConfirm(false);
    onClose();
  };

  const handleLanguageSelect = (languageCode: string) => {
    setDraftLanguage(languageCode);
    setShowLanguageSelector(false);
  };

  const handleSaveSettings = async () => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      // 1. Update user name if changed
      if (draftUserName.trim() && draftUserName !== (userName || "")) {
        await updateUserName(draftUserName.trim());
      }

      // 2. Update pronouns if changed
      if (draftPronouns !== (pronouns || "")) {
        await updatePronouns(draftPronouns);
      }

      // 3. Update theme if changed
      const targetTheme = draftDarkMode ? "dark" : "light";
      const currentIsDark =
        theme === "dark" ||
        (theme === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches);
      if (draftDarkMode !== currentIsDark) {
        setTheme(targetTheme);
      }

      // 4. Update palette if changed
      if (draftPalette !== palette) {
        setPalette(draftPalette);
      }

      // 5. Update language if changed
      if (draftLanguage !== userLanguage) {
        await updateLanguage(draftLanguage);
      }

      // 6. Callback for parent state updates
      onUpdateSettings({
        theme: targetTheme,
        palette: draftPalette,
        autoTranslation: draftAutoTranslation,
        showOriginal,
        anonymousMode,
        primaryLanguage: draftLanguage,
      });

      window.dispatchEvent(new Event("userNameUpdated"));
      toast({
        title: "Settings Saved",
        description: "Your preferences have been saved successfully.",
      });
      onClose();
    } catch (error) {
      console.error("Failed to save settings:", error);
      toast({
        title: "Error",
        description: "Failed to save settings. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleActionClick = (type: "clearData" | "resetOnboarding") => {
    setActionType(type);
    setIsPopupOpen(true);
  };

  const handleConfirm = async () => {
    if (actionType === "clearData") {
      try {
        if (currentUser?.uid) {
          const response = await fetch(apiUrl("/api/users/clear-data"), {
            method: "POST",
            headers: { "Content-Type": "application/json", ...(await getAuthHeaders()) },
            body: JSON.stringify({}),
          });

          if (response.ok) {
            const keysToRemove = [];
            for (let i = 0; i < localStorage.length; i++) {
              const key = localStorage.key(i);
              if (
                key &&
                (key.startsWith("chatMuteStatus_") || key.includes("room-")) &&
                !key.includes("onboarded") &&
                !key.includes("language") &&
                !key.includes("user-name")
              ) {
                keysToRemove.push(key);
              }
            }
            keysToRemove.forEach((key) => localStorage.removeItem(key));
            toast({
              title: "Data Cleared",
              description: "All conversation history has been cleared.",
            });
            onClearCurrentRoom?.();
            onRoomDeleted?.();
          }
        }
      } catch (error) {
        console.error("Error clearing chat data:", error);
      }
    } else if (actionType === "resetOnboarding") {
      localStorage.removeItem("vyzle-chat-language");
      localStorage.removeItem("vyzle-chat-onboarded");
      localStorage.removeItem("vyzle-chat-user-name");
      onClose();
      navigate("/");
    }
    setIsPopupOpen(false);
    setActionType(null);
  };

  const handleCancel = () => {
    setIsPopupOpen(false);
    setActionType(null);
  };

  const popupConfig =
    actionType === "clearData"
      ? {
          title: "Clear All Chat Data?",
          message: "This will permanently delete all your chat history. This action cannot be undone.",
          confirmText: "Yes, clear data",
        }
      : {
          title: "Reset Onboarding?",
          message: "This will clear your name and language settings. You'll need to set them again.",
          confirmText: "Yes, reset",
        };

  const showPrivacyPolicy = () => {
    sessionStorage.setItem("vyzle-settings-reopen", activeTab || "about");
    onClose();
    navigate("/privacy-policy");
  };

  const showTerms = () => {
    sessionStorage.setItem("vyzle-settings-reopen", activeTab || "about");
    onClose();
    navigate("/terms-and-conditions");
  };

  const showSupport = () => {
    sessionStorage.setItem("vyzle-settings-reopen", activeTab || "about");
    onClose();
    navigate("/support");
  };

  const navItems: { id: SettingsTab; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
    { id: "profile", label: "Profile", icon: User },
    { id: "appearance", label: "Appearance", icon: Palette },
    { id: "account", label: "Account", icon: Shield },
    { id: "language", label: "Language", icon: Globe },
    { id: "about", label: "About", icon: Info },
  ];

  return (
    <>
      <Sheet open={isOpen} onOpenChange={(open) => {
        if (!open) {
          handleRequestClose();
        }
      }}>
        <SheetContent
          side="left"
          className="
            fixed inset-0 h-screen h-[100dvh] w-screen w-full max-w-none sm:max-w-none p-0 gap-0 overflow-hidden
            bg-white dark:bg-[#0f172a]
            text-gray-900 dark:text-gray-100
            border-none shadow-none flex flex-col md:flex-row m-0 rounded-none
          "
        >
          {/* Radix Accessible Titles */}
          <SheetTitle className="sr-only">Settings</SheetTitle>
          <SheetDescription className="sr-only">
            Manage your Vyzle chat profile, appearance, account, language, and view about info.
          </SheetDescription>

          {/* Left Sidebar / Top Nav inside Settings Modal */}
          <aside className="w-full md:w-80 shrink-0 h-auto md:h-full bg-gray-50 dark:bg-[#090d16] border-b md:border-b-0 md:border-r border-gray-200 dark:border-gray-800 p-3 sm:p-4 md:p-6 flex flex-col md:justify-between">
            <div>
              <div className="flex items-center gap-3 px-1 pb-3 md:px-2 md:pb-5 mb-2 md:mb-4 border-b border-gray-200 dark:border-gray-800">
                <div className="w-8 h-8 md:w-10 md:h-10 rounded-xl bg-[var(--accent)]/15 flex items-center justify-center text-[var(--accent)]">
                  <Settings className="w-4 h-4 md:w-5 md:h-5" />
                </div>
                <div>
                  <h2 className="text-base md:text-xl font-bold text-gray-900 dark:text-white">Settings</h2>
                  <p className="text-[11px] md:text-xs text-muted-foreground">Manage your preferences</p>
                </div>
              </div>

              {/* Navigation Tabs with Mobile Scroll Arrows */}
              <div className="relative flex items-center w-full">
                {canScrollLeft && (
                  <button
                    type="button"
                    onClick={() => scrollTabs("left")}
                    className="md:hidden absolute left-0 z-20 p-1.5 rounded-full bg-black/75 dark:bg-black/90 text-white shadow-lg backdrop-blur-sm -ml-1 flex items-center justify-center hover:scale-110 active:scale-95 transition-all"
                    aria-label="Scroll options left"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                )}

                <nav
                  ref={tabsNavRef}
                  onScroll={checkScroll}
                  className="flex md:flex-col gap-1.5 overflow-x-auto md:overflow-visible pb-1 md:pb-0 hide-scrollbar w-full scroll-smooth px-1"
                >
                  {navItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = activeTab === item.id;
                    return (
                      <button
                        key={item.id}
                        onClick={() => {
                          setActiveTab(item.id);
                        }}
                        className={`
                          flex items-center gap-2 md:gap-3.5 px-3.5 py-2 md:px-4 md:py-3 rounded-xl text-xs md:text-sm font-medium transition-all whitespace-nowrap shrink-0 md:shrink
                          ${
                            isActive
                              ? "bg-gradient-to-r from-[var(--bubble-own-from)] to-[var(--bubble-own-to)] text-white shadow-md shadow-[var(--accent)]/20 font-semibold"
                              : "text-gray-600 dark:text-gray-400 hover:bg-gray-200/60 dark:hover:bg-gray-800/60 hover:text-gray-900 dark:hover:text-white"
                          }
                        `}
                      >
                        <Icon className={`w-4 h-4 md:w-5 md:h-5 shrink-0 ${isActive ? "text-white" : "text-gray-500 dark:text-gray-400"}`} />
                        <span className="text-xs md:text-sm">{item.label}</span>
                      </button>
                    );
                  })}
                </nav>

                {canScrollRight && (
                  <button
                    type="button"
                    onClick={() => scrollTabs("right")}
                    className="md:hidden absolute right-0 z-20 p-1.5 rounded-full bg-black/75 dark:bg-black/90 text-white shadow-lg backdrop-blur-sm -mr-1 flex items-center justify-center hover:scale-110 active:scale-95 transition-all animate-pulse"
                    aria-label="Scroll options right"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Sidebar bottom version badge */}
            <div className="hidden md:block pt-5 border-t border-gray-200 dark:border-gray-800 text-xs text-gray-400 dark:text-gray-500">
              <p className="font-semibold text-gray-600 dark:text-gray-400">Vyzle Chat</p>
              <p className="text-[11px] mt-0.5">v{versionData.version} (build {versionData.build})</p>
              <p className="text-[10px] text-gray-400 dark:text-gray-600 mt-2">
                Accounts deactivate after 30 days of inactivity.
              </p>
            </div>
          </aside>

          {/* Right / Down Main Content Window */}
          <main className="flex-1 min-h-0 flex flex-col min-w-0 bg-white dark:bg-[#0f172a] h-full overflow-hidden">
            {/* Main Window Header */}
            <div className="px-4 py-3 sm:px-6 sm:py-4 md:px-8 md:py-5 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between shrink-0">
              <div>
                <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 dark:text-white capitalize">
                  {activeTab === "profile" && "Profile Settings"}
                  {activeTab === "appearance" && "Appearance & Theme"}
                  {activeTab === "account" && "Account Management"}
                  {activeTab === "language" && "Language & Translation"}
                  {activeTab === "about" && "About Vyzle"}
                </h3>
                <p className="text-[11px] sm:text-xs text-muted-foreground mt-0.5 sm:mt-1">
                  {activeTab === "profile" && "Manage your display name, pronouns, and translation preferences."}
                  {activeTab === "appearance" && "Customize your color accent gradient and dark/light mode theme."}
                  {activeTab === "account" && "View your authentication status and data deletion options."}
                  {activeTab === "language" && "Choose your primary language for real-time multilingual translation."}
                  {activeTab === "about" && "Version information, terms, privacy, and customer support."}
                </p>
              </div>
            </div>

            {/* Main Window Body */}
            <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6 md:p-8 space-y-5 sm:space-y-6 hide-scrollbar">
              {/* === PROFILE TAB === */}
              {activeTab === "profile" && (
                <div className="space-y-5 sm:space-y-6 max-w-2xl">
                  <div className="space-y-2">
                    <Label className="text-foreground font-semibold text-sm">Display Name</Label>
                    <Input
                      value={draftUserName}
                      onChange={(e) => setDraftUserName(e.target.value)}
                      placeholder="Your name"
                      className="rounded-xl h-10 sm:h-11 border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-[#1e293b]"
                    />
                    <p className="text-xs text-muted-foreground">This name is shown to other users in all conversations.</p>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-foreground font-semibold text-sm">Pronouns</Label>
                    <div className="flex flex-wrap gap-2 pt-1" role="radiogroup" aria-label="Pronouns">
                      {PRONOUN_OPTIONS.map((p) => {
                        const isSelected = draftPronouns === p.value;
                        return (
                          <button
                            key={p.value || "skip"}
                            type="button"
                            role="radio"
                            aria-checked={isSelected}
                            onClick={() => setDraftPronouns(p.value)}
                            className={`px-3.5 py-1.5 sm:px-4 sm:py-2 text-xs rounded-full border transition-all ${
                              isSelected
                                ? "bg-gradient-to-r from-[var(--bubble-own-from)] to-[var(--bubble-own-to)] text-white border-transparent shadow-sm font-semibold"
                                : "bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-[var(--accent)]"
                            }`}
                          >
                            {p.label}
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Helps the AI translate your messages with correct grammatical gender agreement.
                    </p>
                  </div>

                  {currentUser?.isAnonymous && (
                    <div className="p-4 sm:p-5 rounded-2xl bg-gray-50 dark:bg-[#1e293b]/60 border border-gray-200 dark:border-gray-800 space-y-3">
                      <div>
                        <Label className="text-foreground font-semibold text-sm">Guest Profile</Label>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          You are currently using an anonymous session. Upgrade to a permanent account to keep your chats.
                        </p>
                      </div>
                      <Button
                        onClick={() => setShowAuthUpgrade(true)}
                        className="w-full h-10 sm:h-11 rounded-xl text-xs sm:text-sm font-semibold text-white bg-gradient-to-r from-[var(--bubble-own-from)] to-[var(--bubble-own-to)] hover:shadow-lg hover:shadow-[var(--accent)]/30 transition-all"
                      >
                        <Shield className="w-4 h-4 mr-2" />
                        Upgrade Account
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* === APPEARANCE TAB === */}
              {activeTab === "appearance" && (
                <div className="space-y-5 sm:space-y-6 max-w-2xl">
                  <div className="space-y-2">
                    <Label className="text-foreground font-semibold text-sm sm:text-base">Theme Colors</Label>
                    <p className="text-xs text-muted-foreground">
                      Choose your favorite accent gradient applied across chat bubbles, buttons, and highlights.
                    </p>
                    <div className="pt-2">
                      <PaletteSelector value={draftPalette} onChange={setDraftPalette} />
                    </div>
                  </div>

                  <div className="p-4 sm:p-5 rounded-2xl bg-gray-50 dark:bg-[#1e293b]/60 border border-gray-200 dark:border-gray-800 flex items-center justify-between gap-3">
                    <div>
                      <Label className="text-foreground font-semibold text-sm">Dark Mode</Label>
                      <p className="text-xs text-muted-foreground">Switch between Light and Dark interface</p>
                    </div>
                    <Switch
                      checked={draftDarkMode}
                      onCheckedChange={(checked) => {
                        setDraftDarkMode(checked);
                      }}
                      className="data-[state=checked]:bg-[var(--accent)]"
                    />
                  </div>
                </div>
              )}

              {/* === ACCOUNT TAB === */}
              {activeTab === "account" && (
                <div className="space-y-5 sm:space-y-6 max-w-2xl">
                  <div className="p-4 sm:p-5 rounded-2xl bg-gray-50 dark:bg-[#1e293b]/60 border border-gray-200 dark:border-gray-800 space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 sm:gap-3">
                      <div>
                        <Label className="text-foreground font-semibold text-sm">Account Type</Label>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {currentUser?.isAnonymous ? "Anonymous Guest (temporary session)" : "Authenticated User"}
                        </p>
                      </div>
                      <span className="self-start sm:self-auto px-3 py-1 rounded-full text-xs font-semibold bg-[var(--accent)]/15 text-[var(--accent)] border border-[var(--accent)]/30 whitespace-nowrap">
                        {currentUser?.isAnonymous ? "Guest Session" : "Verified Account"}
                      </span>
                    </div>

                    {currentUser?.isAnonymous && (
                      <div className="pt-1">
                        <Button
                          onClick={() => setShowAuthUpgrade(true)}
                          className="w-full h-10 sm:h-11 rounded-xl text-xs sm:text-sm font-semibold text-white bg-gradient-to-r from-[var(--bubble-own-from)] to-[var(--bubble-own-to)] hover:shadow-lg hover:shadow-[var(--accent)]/30 transition-all"
                        >
                          <Shield className="w-4 h-4 mr-2" />
                          Upgrade to Permanent Account
                        </Button>
                      </div>
                    )}
                  </div>

                  <div className="space-y-3 pt-2">
                    <Label className="text-foreground font-semibold text-sm text-red-600 dark:text-red-400">
                      Danger Zone
                    </Label>
                    <div className="p-4 sm:p-5 rounded-2xl border border-red-200 dark:border-red-900/40 bg-red-50/50 dark:bg-red-950/20 space-y-3">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">Clear Chat Data</p>
                          <p className="text-xs text-muted-foreground mt-0.5">Permanently remove all local and active chat rooms</p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleActionClick("clearData")}
                          className="self-start sm:self-auto text-red-600 border-red-300 dark:border-red-800 hover:bg-red-600 hover:text-white rounded-xl transition-colors whitespace-nowrap"
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                          Clear Data
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* === LANGUAGE TAB === */}
              {activeTab === "language" && (
                <div className="space-y-5 sm:space-y-6 max-w-2xl">
                  <div className="space-y-3">
                    <Label className="text-foreground font-semibold text-sm sm:text-base">Primary Chat Language</Label>
                    <p className="text-xs text-muted-foreground">
                      Incoming messages in any language will be translated automatically into this language.
                    </p>

                    <div className="p-4 sm:p-5 rounded-2xl bg-gray-50 dark:bg-[#1e293b]/60 border border-gray-200 dark:border-gray-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
                      <div className="flex items-center gap-3.5 min-w-0">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-[var(--accent)]/15 flex items-center justify-center text-[var(--accent)] font-bold text-sm sm:text-base shrink-0">
                          {draftLanguage.toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-sm sm:text-base text-gray-900 dark:text-white truncate">
                            {getLanguageByCode(draftLanguage)?.name || draftLanguage}
                          </p>
                          <p className="text-xs text-muted-foreground">Selected Language</p>
                        </div>
                      </div>

                      <Button
                        onClick={() => setShowLanguageSelector(true)}
                        className="w-full sm:w-auto rounded-xl font-semibold text-xs h-9 sm:h-10 px-4 sm:px-5 text-white bg-gradient-to-r from-[var(--bubble-own-from)] to-[var(--bubble-own-to)] hover:shadow-md hover:shadow-[var(--accent)]/30 transition-all shrink-0"
                      >
                        Change Language
                      </Button>
                    </div>
                  </div>

                  <div className="p-4 sm:p-5 rounded-2xl bg-gray-50 dark:bg-[#1e293b]/60 border border-gray-200 dark:border-gray-800 flex items-center justify-between gap-3">
                    <div>
                      <Label className="text-foreground font-semibold text-sm">Auto Translation</Label>
                      <p className="text-xs text-muted-foreground">Automatically translate incoming messages</p>
                    </div>
                    <Switch
                      checked={draftAutoTranslation}
                      onCheckedChange={(checked) => {
                        setDraftAutoTranslation(checked);
                      }}
                      className="data-[state=checked]:bg-[var(--accent)]"
                    />
                  </div>
                </div>
              )}

              {/* === ABOUT TAB === */}
              {activeTab === "about" && (
                <div className="space-y-6 max-w-2xl">
                  <div className="p-5 rounded-2xl bg-gray-50 dark:bg-[#1e293b]/60 border border-gray-200 dark:border-gray-800 space-y-3">
                    <div className="flex items-center gap-3.5">
                      <div className="w-12 h-12 rounded-2xl bg-[var(--accent)]/15 flex items-center justify-center text-[var(--accent)] shrink-0">
                        <Info className="w-6 h-6" />
                      </div>
                      <div>
                        <h4 className="font-bold text-base text-gray-900 dark:text-white">Vyzle Chat</h4>
                        <p className="text-xs text-muted-foreground">
                          v{versionData.version} (build {versionData.build})
                        </p>
                      </div>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 pt-2 leading-relaxed">
                      A progressive web application designed for seamless anonymous cross-language communication with real-time AI translation.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-foreground font-semibold text-sm">Links & Resources</Label>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <Button
                        variant="outline"
                        onClick={showPrivacyPolicy}
                        className="w-full justify-center text-xs font-semibold rounded-xl h-10 border-gray-200 dark:border-gray-800 hover:border-[var(--accent)] transition-all"
                      >
                        Privacy Policy
                      </Button>
                      <Button
                        variant="outline"
                        onClick={showTerms}
                        className="w-full justify-center text-xs font-semibold rounded-xl h-10 border-gray-200 dark:border-gray-800 hover:border-[var(--accent)] transition-all"
                      >
                        Terms of Service
                      </Button>
                      <Button
                        variant="outline"
                        onClick={showSupport}
                        className="w-full justify-center text-xs font-semibold rounded-xl h-10 border-gray-200 dark:border-gray-800 hover:border-[var(--accent)] transition-all"
                      >
                        Support
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Main Window Footer with Action Buttons */}
            <div className="px-4 py-3 sm:px-6 sm:py-3.5 md:px-8 md:py-4 border-t border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-[#090d16]/50 flex justify-end gap-2.5 sm:gap-3 shrink-0 mt-auto">
              <Button
                variant="outline"
                onClick={handleRequestClose}
                className="px-4 sm:px-5 h-9 sm:h-10 rounded-xl text-xs font-semibold border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-800 dark:hover:text-white transition-colors"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveSettings}
                disabled={isSaving}
                className="px-5 sm:px-6 h-9 sm:h-10 rounded-xl text-xs font-semibold text-white bg-gradient-to-r from-[var(--bubble-own-from)] to-[var(--bubble-own-to)] hover:shadow-md hover:shadow-[var(--accent)]/30 transition-all disabled:opacity-60"
              >
                {isSaving ? "Saving..." : "Save Settings"}
              </Button>
            </div>
          </main>
        </SheetContent>
      </Sheet>

      {/* Full Language Selector Sub-Dialog */}
      <Dialog open={showLanguageSelector} onOpenChange={setShowLanguageSelector}>
        <DialogContent className="w-full max-w-none h-[100dvh] sm:h-auto sm:max-h-[90vh] sm:max-w-4xl overflow-hidden flex flex-col p-0 rounded-none sm:rounded-2xl">
          <DialogHeader className="px-4 py-3 border-b border-gray-200 dark:border-gray-800">
            <DialogTitle className="text-base font-semibold">Choose Your Language</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Select your preferred language for instant AI translation.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-4 py-4 hide-scrollbar">
            <LanguageSelector
              onLanguageSelect={handleLanguageSelect}
              selectedLanguage={draftLanguage}
              className="w-full"
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Discard Unsaved Changes Confirmation Popup */}
      {showDiscardConfirm && (
        <ConfirmPopup
          isOpen={showDiscardConfirm}
          title="Discard Unsaved Changes?"
          message="You have unsaved changes. Are you sure you want to return without saving?"
          confirmText="Discard Changes"
          cancelText="Keep Editing"
          onConfirm={handleDiscardConfirm}
          onCancel={() => setShowDiscardConfirm(false)}
        />
      )}

      {/* Clear Data / Reset Onboarding Confirmation Popup */}
      {isPopupOpen && (
        <ConfirmPopup
          isOpen={isPopupOpen}
          title={popupConfig.title}
          message={popupConfig.message}
          confirmText={popupConfig.confirmText}
          cancelText="Cancel"
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}

      {/* Auth Upgrade Modal */}
      <AuthUpgradeModal
        isOpen={showAuthUpgrade}
        onClose={() => setShowAuthUpgrade(false)}
        currentUser={currentUser}
      />
    </>
  );
}